import type { IMappedAsset } from "@sundae/wallet-react";
import type { AssetAmount } from "@sundaeswap/asset";
import type { ITxBuilder, ITxBuilderSubmit } from "@sundaeswap/core";
import type { TSwapOutcome } from "@sundaeswap/cpp";
import { Dispatch, ReactNode } from "react";

import { PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import { useOrderMode } from "../../hooks/useExchangeMode";
import { useOrderType } from "../../hooks/useOrderType";
import { IAssetMetaData } from "../../types/Asset.types";
import { TransactionErrorHandler } from "../../utils/transactions/TransactionErrorHandler.class";

export enum EExchangeActions {
  SET_ACTIVE_INTERVAL = "SET_ACTIVE_INTERVAL",
  SET_ASSET_CHANGE_SCOPE = "SET_ASSET_CHANGE_SCOPE",
  SET_DISPLAY_ELEMENTS = "SET_DISPLAY_ELEMENTS",
  SET_DISPLAY_IMAGES = "SET_DISPLAY_IMAGES",
  SET_TRANSACTION_ERROR_MESSAGE = "SET_TRANSACTION_ERROR_MESSAGE",
  SET_TRANSACTION_ERROR_HANDLER = "SET_TRANSACTION_ERROR_HANDLER",
  SET_TRANSACTION_ERROR_ACTIONS = "SET_TRANSACTION_ERROR_ACTIONS",
  SET_FORMATTED_LIMIT_VALUE = "SET_FORMATTED_LIMIT_VALUE",
  SET_FORMATTED_VALUE_ASSET_A = "SET_FORMATTED_VALUE_ASSET_A",
  SET_FORMATTED_VALUE_ASSET_B = "SET_FORMATTED_VALUE_ASSET_B",
  SET_INPUT_EXCEEDS_RESERVE = "SET_INPUT_EXCEEDS_RESERVE",
  SET_OUTPUT_EXCEEDS_RESERVE = "SET_OUTPUT_EXCEEDS_RESERVE",
  SET_IS_ASSET_CONSENT_CHECKED = "SET_IS_ASSET_CONSENT_CHECKED",
  SET_IS_INSUFFICIENT_A = "SET_IS_INSUFFICIENT_A",
  SET_IS_INSUFFICIENT_B = "SET_IS_INSUFFICIENT_B",
  SET_IS_REMAINING_ADA_BELOW_THRESHOLD = "SET_IS_REMAINING_ADA_BELOW_THRESHOLD",
  SET_LATEST_TX_HASH = "SET_ACTIVE_TX_HASH",
  SET_LIMIT_VALUE = "SET_LIMIT_VALUE",
  SET_PARITY = "SET_PARITY",
  SET_SWAP_OUTCOME = "SET_SWAP_OUTCOME",
  SET_TRADE_WARNING = "SET_TRADE_WARNING",
  SET_TRANSACTION_BUILDER_BUILT_TX = "SET_TRANSACTION_BUILDER_BUILT_TX",
  SET_TRANSACTION_BUILDER_COMPLETE_TX = "SET_TRANSACTION_BUILDER_COMPLETE_TX",
  SET_TRANSACTION_BUILDER_STATE = "SET_TRANSACTION_BUILDER_STATE",
  SET_VALUE_ASSET_A = "SET_VALUE_ASSET_A",
  SET_VALUE_ASSET_B = "SET_VALUE_ASSET_B",
  SET_CURRENT_PAGE = "SET_CURRENT_PAGE",
  INCREASE_PAGE = "INCREASE_PAGE",
}

/* -------------------------------------------------------------------------------------------------
 * Chart
 * -----------------------------------------------------------------------------------------------*/
export interface INormalizedTick {
  date: string;
  close: number;
  open: number;
  high: number;
  low: number;
  volume: string;
}
export const EXCHANGE_CHART_ID = "SUNDAESWAP_EXCHANGE_CHART";
export const EXCHANGE_CHART_HEADER_ID = "SUNDAESWAP_EXCHANGE_CHART_HEADER";
export type TInterval = "all" | "daily" | "weekly" | "monthly" | "ytd";

/* -------------------------------------------------------------------------------------------------
 * General Exchange Types
 * -----------------------------------------------------------------------------------------------*/
export enum ETradeWarning {
  none = "none",
  both = "both",
  indivisible = "indivisible",
  unregistered = "unregistered",
}

export enum ETransactionBuilderState {
  BUILD = "BUILD",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

/* -------------------------------------------------------------------------------------------------
 * State
 * -----------------------------------------------------------------------------------------------*/
export interface IExchangeAction {
  type: string;
  // Payload types are inforced in ./actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

export interface ITransactionErrorActions {
  resolutionAction: () => void;
  resolutionButtonText: ReactNode;
}

export interface IExchangeState {
  activeInterval: TInterval; // used for the chart
  activePool?: PoolFragmentFragment;
  assetChangeScope?: "activeAssetA" | "activeAssetB";
  displayElements?: boolean;
  transactionErrorActions?: ITransactionErrorActions;
  transactionErrorMessage?: ReactNode;
  transactionErrorHandler?: TransactionErrorHandler;
  formattedLimitValue?: string;
  formattedValueAssetA: string;
  formattedValueAssetB: string;
  outputExceedsReserve: boolean;
  isAssetConsentChecked?: boolean; // required for unregistered / indivisible assets --> consent is needed to proceed with the trade
  isInsufficientAssetA: boolean;
  isInsufficientAssetB: boolean;
  isRemainingAdaAmountBelowThreshold: boolean; // used for rendering a warning if the users ADA amount falls below `MIN_ADA_WALLET_BALANCE` after transaction
  limitValue?: string;
  parity?: AssetAmount;
  swapOutcome?: TSwapOutcome;
  tradeWarning: ETradeWarning; // renders a warning for indivisible / unregistered assets
  valueAssetA: string;
  valueAssetB: string;
  pageSize: number;
  page: number;

  // these values are used for building the tx upfront and not during the mutation
  // @TODO: refactor tx handling to global state instead of scoped flows
  transactionBuilderBuiltTx?: ITxBuilder;
  transactionBuilderCompleteTx?: ITxBuilderSubmit;
  transactionBuilderState?: ETransactionBuilderState;
}

export interface IExchangeContext {
  activeAssetA?: IMappedAsset<IAssetMetaData>;
  activeAssetAId?: string;
  activeAssetB?: IMappedAsset<IAssetMetaData>;
  activeAssetBId?: string;
  assetADecimals?: number;
  assetBDecimals?: number;
  assetsWithBalance?: IMappedAsset<IAssetMetaData>[];
  assetWarning?: string;
  availablePools: PoolFragmentFragment[] | null;
  balanceLoaded: boolean;
  checkInsufficientAssetA: (value: string) => void;
  checkInsufficientAssetB: (value: string) => void;
  dispatch: Dispatch<IExchangeAction>;
  exchangeModeFromSearch?: ReturnType<typeof useOrderMode>;
  exchangeOrderTypeFromSearch?: ReturnType<typeof useOrderType>;
  inputAssetList?: IMappedAsset<IAssetMetaData>[];
  isFetched: boolean;
  isLoading: boolean;
  isRefetching: boolean;
  isValidLimitValue: boolean;
  isWalletConnected: boolean;
  outputAssetList?: IMappedAsset<IAssetMetaData>[];
  resetContext: () => void;
  resetLimitOrderState: () => void;
  setLimitValueToInitialRatio: () => void;
  state: IExchangeState;
}
